import React from "react";
import { Sun, Moon, Laptop, Check } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const OPTIONS = [
  { v: "light", key: "theme.light", icon: Sun },
  { v: "dark", key: "theme.dark", icon: Moon },
  { v: "system", key: "theme.system", icon: Laptop },
];

/* Compact icon toggle for the header (light/dark/system) */
export const ThemeToggle = ({ className }) => {
  const { mode, effective, setMode } = useTheme();
  const { t } = useI18n();
  const Icon = effective === "dark" ? Moon : Sun;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="theme-toggle-bouton"
          aria-label={`${t("theme.label")}: ${t(`theme.${mode}`)}`}
          className={cn("relative rounded-full p-2 text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}
        >
          <Icon className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuLabel>{t("theme.label")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map((o) => (
          <DropdownMenuItem key={o.v} onClick={() => setMode(o.v)} data-testid={`theme-option-${o.v}`} className="gap-2">
            <o.icon className="h-4 w-4" /> {t(o.key)}
            {mode === o.v && <Check className="ml-auto h-4 w-4 text-secondary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/* Segmented control for the profile settings */
export const ThemeSegmented = ({ className }) => {
  const { mode, setMode } = useTheme();
  const { t } = useI18n();
  return (
    <div className={cn("inline-flex rounded-lg border p-0.5", className)} role="group" aria-label={t("theme.label")} data-testid="theme-segmented">
      {OPTIONS.map((o) => (
        <button
          key={o.v}
          onClick={() => setMode(o.v)}
          aria-pressed={mode === o.v}
          data-testid={`theme-seg-${o.v}`}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            mode === o.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <o.icon className="h-4 w-4" /> {t(o.key)}
        </button>
      ))}
    </div>
  );
};
